import { useState, useEffect } from 'react';
import { useData } from '../context/DataContext';
import { getReviews, updateReview, deleteReview } from '../api/api';
import AdminLayout from './AdminLayout';
import './Admin.css';

export default function AdminReviews({ onLogout }) {
  const { products } = useData();
  const [reviews, setReviews] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getReviews();
      setReviews(data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      setError(error.message || 'Failed to load reviews. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : `Product #${productId}`;
  };

  const handleStatusChange = async (reviewId, newStatus) => {
    try {
      await updateReview(reviewId, { status: newStatus }, token);
      await fetchReviews();
    } catch (error) {
      alert('Failed to update review status: ' + error.message);
    }
  };

  const handleDelete = async (reviewId) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await deleteReview(reviewId, token);
        await fetchReviews();
      } catch (error) {
        alert('Failed to delete review: ' + error.message);
      }
    }
  };

  const filteredReviews = filterStatus === 'all'
    ? reviews
    : reviews.filter(r => r.status === filterStatus);

  const renderStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  return (
    <AdminLayout onLogout={onLogout}>
      <div className="admin-header">
        <h1>Reviews</h1>
        <p>Moderate customer reviews</p>
      </div>

      <div className="admin-actions">
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ fontWeight: '500' }}>Filter by status:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="all">All Reviews ({reviews.length})</option>
            <option value="pending">Pending ({reviews.filter(r => r.status === 'pending').length})</option>
            <option value="approved">Approved ({reviews.filter(r => r.status === 'approved').length})</option>
            <option value="rejected">Rejected ({reviews.filter(r => r.status === 'rejected').length})</option>
          </select>
        </div>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Customer</th>
              <th>Rating</th>
              <th>Title</th>
              <th>Review</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReviews.map(review => (
              <tr key={review.id}>
                <td>{getProductName(review.productId)}</td>
                <td>{review.customerName}</td>
                <td style={{ fontSize: '18px', color: '#f5a623' }}>
                  {renderStars(review.rating)}
                </td>
                <td><strong>{review.title}</strong></td>
                <td style={{ maxWidth: '300px' }}>
                  {review.review.length > 100
                    ? review.review.substring(0, 100) + '...'
                    : review.review}
                </td>
                <td>{review.date}</td>
                <td>
                  <span
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background:
                        review.status === 'approved' ? '#28a745' :
                        review.status === 'pending' ? '#ffc107' :
                        '#dc3545',
                      color: 'white'
                    }}
                  >
                    {review.status.toUpperCase()}
                  </span>
                </td>
                <td>
                  <div className="table-actions">
                    {review.status !== 'approved' && (
                      <button
                        className="btn-edit"
                        onClick={() => handleStatusChange(review.id, 'approved')}
                        style={{ background: '#28a745' }}
                      >
                        Approve
                      </button>
                    )}
                    {review.status !== 'rejected' && (
                      <button
                        className="btn-delete"
                        onClick={() => handleStatusChange(review.id, 'rejected')}
                        style={{ background: '#ffc107', color: '#000' }}
                      >
                        Reject
                      </button>
                    )}
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(review.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="data-list">
        {filteredReviews.map(review => (
          <div key={review.id} className="data-list-item">
            <div className="data-list-content">
              <div style={{ width: '100%' }}>
                <h3 className="data-list-title">{review.title}</h3>
                <div className="data-list-meta">
                  <span style={{ fontSize: '18px', color: '#f5a623' }}>
                    {renderStars(review.rating)}
                  </span>
                  <span className="data-list-separator">•</span>
                  <span>{review.customerName}</span>
                </div>
                <p style={{ margin: '0.5rem 0', color: '#666' }}>{review.review}</p>
                <div style={{ display: 'flex', gap: '10px', marginTop: '0.5rem', fontSize: '13px' }}>
                  <span><strong>Product:</strong> {getProductName(review.productId)}</span>
                  <span><strong>Date:</strong> {review.date}</span>
                  <span>
                    <strong>Status:</strong>{' '}
                    <span
                      style={{
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background:
                          review.status === 'approved' ? '#28a745' :
                          review.status === 'pending' ? '#ffc107' :
                          '#dc3545',
                        color: 'white'
                      }}
                    >
                      {review.status.toUpperCase()}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div className="data-list-actions">
              {review.status !== 'approved' && (
                <button
                  className="btn-edit"
                  onClick={() => handleStatusChange(review.id, 'approved')}
                  style={{ background: '#28a745' }}
                >
                  Approve
                </button>
              )}
              {review.status !== 'rejected' && (
                <button
                  className="btn-delete"
                  onClick={() => handleStatusChange(review.id, 'rejected')}
                  style={{ background: '#ffc107', color: '#000' }}
                >
                  Reject
                </button>
              )}
              <button
                className="btn-delete"
                onClick={() => handleDelete(review.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
          <p>Loading reviews...</p>
        </div>
      )}

      {error && !loading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#dc3545', background: '#fff3f3', borderRadius: '8px', margin: '2rem 0' }}>
          <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Error Loading Reviews</p>
          <p style={{ fontSize: '0.9rem' }}>{error}</p>
          <button
            onClick={fetchReviews}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1.5rem',
              background: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '500'
            }}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && filteredReviews.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
          <p>No reviews found.</p>
        </div>
      )}
    </AdminLayout>
  );
}
